import { Virtualizer } from '@tanstack/react-virtual';
import { useEffect, useLayoutEffect, useRef } from 'react';

import { isValidScrollToIndex } from '../model';

/**
 * Scrolls once per `scrollToKey` (defaults to the index). Later index shifts
 * for the same key, e.g. expanding a section above the target, do not scroll
 * the user back.
 */
export function useScrollToVirtualIndex(
  virtualizer: Virtualizer<HTMLElement, Element>,
  scrollParent: HTMLElement | null,
  scrollToIndex: number | null | undefined,
  scrollToKey?: string | null
): void {
  const lastScrolledRef = useRef<{
    key: string;
    parent: HTMLElement;
  } | null>(null);
  const frameRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (!isValidScrollToIndex(scrollToIndex) || !scrollParent) {
      return;
    }

    const key = scrollToKey ?? String(scrollToIndex);
    const last = lastScrolledRef.current;
    if (last && last.key === key && last.parent === scrollParent) {
      return;
    }
    lastScrolledRef.current = { key, parent: scrollParent };

    virtualizer.scrollToIndex(scrollToIndex, { align: 'start' });
    if (frameRef.current !== null) {
      window.cancelAnimationFrame(frameRef.current);
    }
    frameRef.current = window.requestAnimationFrame(() => {
      frameRef.current = null;
      virtualizer.scrollToIndex(scrollToIndex, { align: 'start' });
    });
  }, [scrollToIndex, scrollToKey, scrollParent, virtualizer]);

  useEffect(
    () => () => {
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
      }
    },
    []
  );
}
